import { useEffect } from 'react'
import { Snackbar, Alert } from '@mui/material'
import { useAuthStore } from '../store/authStore'
import { useEnumStore } from '../store/enumStore'
import { metadataService } from '../api/adminServices'
import type { Language } from '../store/settingsStore'

function toLanguage(locale: string | undefined): Language | null {
  if (locale === 'EN' || locale === 'HE' || locale === 'AR' || locale === 'RU') {
    return locale
  }
  return null
}

/**
 * Loads enum metadata using the authenticated user's server locale (JWT),
 * same as Driver/Shipper — no locale query param.
 */
export default function EnumLoader() {
  const { isAuthenticated, user } = useAuthStore()
  const userLocale = toLanguage(user?.locale)
  const { enums, loadedLocale, setEnums, error, setError } = useEnumStore()

  useEffect(() => {
    if (!isAuthenticated || !userLocale) return
    if (enums && loadedLocale === userLocale) return

    let cancelled = false

    metadataService
      .getEnums()
      .then((data) => {
        if (!cancelled) {
          setEnums(data, userLocale)
        }
      })
      .catch((err) => {
        if (cancelled) return
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load metadata enums'
        console.error('Failed to load enums:', err)
        setError(message)
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, userLocale, enums, loadedLocale, setEnums, setError])

  return (
    <Snackbar
      open={!!error}
      autoHideDuration={10000}
      onClose={() => setError(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity="error" onClose={() => setError(null)} variant="filled">
        {error}
      </Alert>
    </Snackbar>
  )
}
